import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {S3Service} from '../../../../core/services/s3.service';
import {RaceApiService} from '../../../../core/services/race-api.service';
import {ActivatedRoute, Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Race, RaceRankingMethod, RaceType} from '../../../../core/model/resources/race';
import {FormControl, Validators} from '@angular/forms';
import {v4} from 'uuid';
import {CouponApiService, CreateCouponInput} from '../../../../core/services/coupon-api.service';
import moment from 'moment';
import {MessageDialogComponent} from '../../../partials/message-dialog/message-dialog.component';
import {ALL_USER_COUPON_URL, CouponDistributionConditionType} from 'src/app/core/model/resources/coupon';
import {ActivityType} from '../../../../core/model/resources/activity';
import {GeoJson} from '../../../../core/model/geo-json';
import {FullscreenControl, LngLatBounds, Map} from 'mapbox-gl';
import * as area from '@turf/area';
import {UtilService} from '../../../../core/services/util.service';
import {flatten, flattenDepth} from 'lodash-es';
import {environment} from '../../../../../environments/environment';
import {ActivityDaysChallengeInput, CalorieChallengeInput, DistanceChallengeInput} from '../../../../core/services/batch-api.service';
import gjv from 'geojson-validation';

interface ImageSize {
  width: number;
  height: number;
}

@Component({
    selector: 'app-coupon-create',
    templateUrl: './coupon-create.component.html',
    styleUrls: ['./coupon-create.component.scss'],
    standalone: false
})
export class CouponCreateComponent implements OnInit, OnDestroy {

  readonly CouponDistributionConditionType = CouponDistributionConditionType;
  readonly RaceType = RaceType;
  readonly RaceRankingMethod = RaceRankingMethod;
  readonly maxLengthTitle = 50;
  readonly maxLengthDesc = 300;
  readonly maxLengthOther = 30000;

  raceId?: number = this.route.snapshot.paramMap.get('raceId') ? Number(this.route.snapshot.paramMap.get('raceId')) : undefined;
  race?: Race;
  isAllUserCoupon: boolean = this.route.snapshot.url[0].path === ALL_USER_COUPON_URL;
  title: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(1), Validators.maxLength(this.maxLengthTitle)],
    nonNullable: true,
  });
  description: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(1), Validators.maxLength(this.maxLengthDesc)],
    nonNullable: true,
  });
  objectShop: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(1), Validators.maxLength(this.maxLengthOther)],
    nonNullable: true,
  });
  notes: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(1), Validators.maxLength(this.maxLengthOther)],
    nonNullable: true,
  });
  confirmation: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(1), Validators.maxLength(this.maxLengthOther)],
    nonNullable: true,
  });

  @ViewChild('qrCodeImageFile') public qrCodeImageFile?: ElementRef;
  qrCodeImagePreviewUrl: string | undefined = undefined;
  qrCodeImageId: string | null = null;
  qrCodeImageWidth: number | null = null;
  qrCodeImageHeight: number | null = null;
  qrCodeImageFileName: string | null = null;
  qrCodeImageContentType: string | null = null;
  qrCodeImageContentSize: number | null = null;

  expirationDate: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.nullValidator],
    nonNullable: true,
  });
  beginningDistributionTerm: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.nullValidator],
    nonNullable: true,
  });
  endOfDistributionTerm: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.nullValidator],
    nonNullable: true,
  });
  rankingByRunning: boolean = false;
  rankingByTrailRunning: boolean = false;
  rankingByCycling: boolean = false;
  rankingByWalking: boolean = false;
  rankingByHiking: boolean = false;
  targetYearMonth: FormControl<string> = new FormControl<string>('', {
    validators: [Validators.required, Validators.nullValidator],
    nonNullable: true,
  });
  distributionConditionType = CouponDistributionConditionType.distance;
  distributionConditionValue: FormControl<number | null> = new FormControl<number | null>(null, {
    validators: [Validators.required, Validators.min(0)],
    nonNullable: false,
  });
  limitEffectiveTime: boolean = true;
  effectiveTime: FormControl<number | null> = new FormControl<number | null>(null, {
    validators: [Validators.required, Validators.min(1)],
    nonNullable: false,
  });
  isPushEnabled: boolean = true;
  pushNotificationMessage = new FormControl<string>('', {
    validators: [Validators.required, Validators.minLength(1)],
    nonNullable: true,
  });

  @ViewChild('geoJSONFile') public geoJSONFile?: ElementRef;
  @ViewChild('specifiedAreaMap') specifiedAreaMap?: ElementRef;
  specifiedAreaSquareMeter?: number;
  specifiedArea?: GeoJson;
  map?: Map;
  mapStyle: string = 'width: 100%;';

  submitted = false;

  constructor(
    private s3Service: S3Service,
    private raceApiService: RaceApiService,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private couponApiService: CouponApiService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.setUpDateDefaultValues();

    if (!!this.raceId && !this.isAllUserCoupon) {
      await this.fetchRace();

      if (!this.race) {
        return;
      }

      // TODO イベント種別が増えたら対応
      if (this.race.ranking_method === RaceRankingMethod.RankingByTime) {
        this.distributionConditionType = CouponDistributionConditionType.time;
      } else if (this.race.ranking_method === RaceRankingMethod.RankingByArea) {
        this.distributionConditionType = CouponDistributionConditionType.area;
      } else if (this.race.ranking_method === RaceRankingMethod.RankingByCalorieChallenge) {
        this.distributionConditionType = CouponDistributionConditionType.calorie;
      } else if (this.race.ranking_method === RaceRankingMethod.RankingByActivityDays) {
        this.distributionConditionType = CouponDistributionConditionType.activityDays;
      }
    }
  }

  async fetchRace(): Promise<void> {
    const result = await this.raceApiService.getById(this.raceId!);
    if (result.success) {
      this.race = result.data as Race;
    } else {
      MessageDialogComponent.createErrorDialog(this.dialog, '大会/イベント情報が取得できませんでした。');
    }
  }

  setUpDateDefaultValues(): void {
    const dateFormat: string = this.isAllUserCoupon ? 'YYYY-MM-DD' : 'YYYY-MM-DDTHH:mm:ss';
    const startOfToday: moment.Moment = moment().hour(0).minute(0).second(0).millisecond(0);
    const startOfTodayString: string = startOfToday.format(dateFormat);
    const endOfToday: moment.Moment = moment().hour(23).minute(59).second(59).millisecond(0);
    const endOfTodayString: string = endOfToday.format(dateFormat);
    this.expirationDate.setValue(endOfToday.format('YYYY-MM-DDTHH:mm:ss'));
    this.beginningDistributionTerm.setValue(startOfTodayString);
    this.endOfDistributionTerm.setValue(endOfTodayString);
  }

  async onQrCodeImageChanged(): Promise<void> {
    const file: File = this.qrCodeImageFile!.nativeElement.files[0];
    if (file.size > 1024 * 500) {
      MessageDialogComponent.createErrorDialog(this.dialog, '画像ファイルは500KB以内にしてください。');
      return;
    }
    if (this.qrCodeImagePreviewUrl) {
      const isOK = await MessageDialogComponent.createPositiveConfirmDialog(this.dialog, '画像を入れ替えます。この操作は即時反映されます。よろしいですか？');
      if (!isOK) {
        return;
      }
    }

    const id = v4().toString();
    const imageSize = await this.getImageSize(file);
    try {
      await this.s3Service.putStoreImage(id, file);
      if (this.qrCodeImagePreviewUrl) {
        await this.s3Service.deleteStoreImageFromUrl(this.qrCodeImagePreviewUrl);
      }
      this.qrCodeImagePreviewUrl = this.s3Service.getStoreImageUrl(id);
      this.qrCodeImageId = id;
      this.qrCodeImageFileName = file.name;
      this.qrCodeImageContentSize = file.size;
      this.qrCodeImageContentType = file.type;
      this.qrCodeImageWidth = imageSize.width;
      this.qrCodeImageHeight = imageSize.height;
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, '画像アップロードに失敗しました。再度選択してください。');
    }
  }

  isConditionTypeSpecifiedArea(): boolean {
    return this.distributionConditionType === CouponDistributionConditionType.specifiedArea;
  }

  async onGeoJSONChanged(): Promise<void> {
    const file: File = this.geoJSONFile!.nativeElement.files[0];
    const geoJSON: any = JSON.parse(await file.text());
    if (!gjv.valid(geoJSON)) {
      MessageDialogComponent.createErrorDialog(this.dialog, 'GeoJSON形式ではありません。');
      return;
    }
    const polygon = geoJSON.features.find((feature: any) => feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon');
    if (!polygon) {
      MessageDialogComponent.createErrorDialog(this.dialog, 'ポリゴンデータが指定されていません。');
      return;
    }
    this.specifiedArea = geoJSON;
    this.specifiedAreaSquareMeter = area.default(geoJSON);
    this.drawMap(geoJSON);
  }

  private drawMap(geoJSON: GeoJson): void {
    this.mapStyle = 'width: 100%; height: 500px;';
    this.map = new Map({
      accessToken: environment.mapboxAccessToken,
      container: this.specifiedAreaMap!.nativeElement,
      style: 'mapbox://styles/rbs-netadmin/cj458yrji09xg2smykk0x8z3z',
    });
    this.map.addControl(new FullscreenControl());
    this.map.on('load', () => {
      let coordinates: [number, number][] = [];
      geoJSON.features.forEach((feature, index) => {
        if (feature.geometry.type === 'Polygon') {
          UtilService.addPolygon(this.map!, `polygon${index}`, feature.geometry.coordinates);
          coordinates = coordinates.concat(flatten<[number, number]>(feature.geometry.coordinates));
        } else if (feature.geometry.type === 'MultiPolygon') {
          UtilService.addMultiPolygon(this.map!, `polygon${index}`, feature.geometry.coordinates);
          coordinates = coordinates.concat(flattenDepth<[number, number]>(feature.geometry.coordinates, 2));
        }
      });
      const bounds = coordinates.reduce((newBounds, coord) => {
        return newBounds.extend(coord);
      }, new LngLatBounds(coordinates[0], coordinates[0]));
      this.map!.fitBounds(bounds, {
        padding: 20,
        maxDuration: 2
      });
    });
    this.map.on('idle', () => {
      this.map!.resize();
    });
  }

  getDistributionTermType(): string {
    return this.isAllUserCoupon ? 'date' : 'datetime-local';
  }

  getTotalSquareMeter(): string {
    return Math.round(this.specifiedAreaSquareMeter!).toLocaleString();
  }

  async onSubmit(): Promise<void> {
    if (this.race && !this.isAllUserCoupon) {
      // TODO ランキング種別を増やした際に対応
      if (this.race.ranking_method === RaceRankingMethod.RankingByDistanceChallenge) {
        const distanceChallengeSetting: DistanceChallengeInput | null = await this.s3Service.getDistanceChallengeInput(this.race.id);
        if (distanceChallengeSetting === null) {
          MessageDialogComponent.createErrorDialog(this.dialog, `設定ファイルの取得に失敗しました。`);
          return;
        }
        if (this.distributionConditionValue.value === null || distanceChallengeSetting.distance < this.distributionConditionValue.value) {
          MessageDialogComponent.createErrorDialog(this.dialog,
            `${this.getDistributionCondition()}は${distanceChallengeSetting.distance}以下の値を設定してください。`);
          return;
        }
      } else if (this.race.ranking_method === RaceRankingMethod.RankingByCalorieChallenge) {
        const calorieChallengeSetting: CalorieChallengeInput | null = await this.s3Service.getCalorieChallengeInput(this.race.id);
        if (this.distributionConditionValue.value === null || calorieChallengeSetting!.kcal < this.distributionConditionValue.value) {
          MessageDialogComponent.createErrorDialog(this.dialog,
            `${this.getDistributionCondition()}は${calorieChallengeSetting!.kcal}以下の値を設定してください。`);
          return;
        }
      } else if (this.race.ranking_method === RaceRankingMethod.RankingByActivityDays) {
        const activityDaysChallengeSetting: ActivityDaysChallengeInput | null = await this.s3Service.getActivityDaysChallengeInput(this.race.id);
        if (activityDaysChallengeSetting === null) {
          MessageDialogComponent.createErrorDialog(this.dialog, `設定ファイルの取得に失敗しました。`);
          return;
        }
        if (this.distributionConditionValue.value === null || activityDaysChallengeSetting.targetDays < this.distributionConditionValue.value) {
          MessageDialogComponent.createErrorDialog(this.dialog,
            `${this.getDistributionCondition()}は${activityDaysChallengeSetting.targetDays}以下の値を設定してください。`);
          return;
        }
      }
      if (this.distributionConditionType === CouponDistributionConditionType.specifiedArea && !this.specifiedArea) {
        MessageDialogComponent.createErrorDialog(this.dialog, `指定エリアのGeoJSONを設定してください。`);
        return;
      }
    } else {
      if (!this.rankingByRunning && !this.rankingByTrailRunning && !this.rankingByCycling && !this.rankingByWalking &&
        !this.rankingByHiking) {
        MessageDialogComponent.createErrorDialog(this.dialog, `アクティビティ種別を一つ以上選択してください。`);
        return;
      }

      if (!this.targetYearMonth.value) {
        MessageDialogComponent.createErrorDialog(this.dialog, `集計対象年月を設定してください。`);
        return;
      }
    }

    const isOK = await MessageDialogComponent.createPositiveConfirmDialog(this.dialog, '登録します。よろしいですか？');
    if (!isOK) {
      return;
    }
    const createCouponInput: CreateCouponInput = {
      title: this.title.value,
      raceId: !this.isAllUserCoupon && !!this.raceId ? this.raceId : undefined,
      description: this.description.value,
      objectShop: this.objectShop.value,
      notes: this.notes.value,
      confirmation: this.confirmation.value,
      qrCodeImageId: this.qrCodeImageId!,
      qrCodeImageWidth: this.qrCodeImageWidth!,
      qrCodeImageHeight: this.qrCodeImageHeight!,
      qrCodeImageFileName: this.qrCodeImageFileName!,
      qrCodeImageContentType: this.qrCodeImageContentType!,
      qrCodeImageContentSize: this.qrCodeImageContentSize!,
      expirationDate: Date.parse(this.expirationDate.value),
      beginningDistributionTerm: this.isAllUserCoupon ? UtilService.jstStrToEpochTimeMsec(this.beginningDistributionTerm.value, 'YYYY-MM-DD') : Date.parse(this.beginningDistributionTerm.value),
      endOfDistributionTerm: this.isAllUserCoupon ? UtilService.jstStrToEpochTimeMsec(this.endOfDistributionTerm.value, 'YYYY-MM-DD') + 1000 * 60 * 60 * 24 - 1 : Date.parse(this.endOfDistributionTerm.value),
      distributionConditionType: this.distributionConditionType,
      distributionConditionValue: this.distributionConditionValue.value!,
      effectiveTime: this.limitEffectiveTime ? (this.effectiveTime.value ?? 0) : 0,
      usePushNotification: this.isPushEnabled,
      pushNotificationMessage: this.pushNotificationMessage.value,
    };

    if (this.distributionConditionType === CouponDistributionConditionType.specifiedArea) {
      createCouponInput.specifiedArea = this.specifiedArea;
    }

    if (this.isAllUserCoupon) {
      const targetActivityTypes = [];
      if (this.rankingByRunning) {
        targetActivityTypes.push(ActivityType.RUNNING);
      }
      if (this.rankingByTrailRunning) {
        targetActivityTypes.push(ActivityType.TRAIL_RUNNING);
      }
      if (this.rankingByCycling) {
        targetActivityTypes.push(ActivityType.CYCLING);
      }
      if (this.rankingByWalking) {
        targetActivityTypes.push(ActivityType.WALKING);
      }
      if (this.rankingByHiking) {
        targetActivityTypes.push(ActivityType.HIKING);
      }
      createCouponInput.distributionAllUserCouponSettings = {
        targetActivityTypes,
        targetYearMonth: Date.parse(this.targetYearMonth.value),
      };
    }

    const result = await this.couponApiService.create(createCouponInput);
    if (result.success) {
      this.submitted = true;
      this.snackBar.open('追加しました', undefined, {
        duration: 5000
      });
      await this.router.navigate(['../../'], {relativeTo: this.route, queryParams: {tabIndex: 2}});
    } else {
      MessageDialogComponent.createErrorDialog(this.dialog, result.error!);
    }
  }

  async getImageSize(imageFile: File): Promise<ImageSize> {
    return new Promise<ImageSize>(((resolve, reject): void => {
      const workImage = new Image();
      workImage.onload = (a): void => {
        resolve({
          width: workImage.width,
          height: workImage.height
        });
      };
      workImage.src = URL.createObjectURL(imageFile);
    }));
  }

  getDistributionCondition(): string {
    // TODO ランキング種別を増やした際に対応
    switch (this.distributionConditionType) {
      case CouponDistributionConditionType.time:
        return '配布条件(閾値)(秒)';
      case CouponDistributionConditionType.area:
        return '配布条件(閾値)(㎡)';
      case CouponDistributionConditionType.calorie:
        return '配布条件(閾値)(kcal)';
      case CouponDistributionConditionType.activityDays:
        return '配布条件(閾値)(日)';
      case CouponDistributionConditionType.distance:
      case CouponDistributionConditionType.groupDistance:
      default:
        return '配布条件(閾値)(km)';
    }
  }

  async ngOnDestroy() {
    if (!this.submitted && this.qrCodeImagePreviewUrl) {
      await this.s3Service.deleteStoreImageFromUrl(this.qrCodeImagePreviewUrl);
    }
  }

}
